import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import {
  getDashboardSummary,
  getOrdersTimeline,
  getAiPerformance,
  getBranchesStats,
} from '../../services/dashboardService'
import type { DashboardSummary, TimelinePoint, AiPerformance, BranchStatRow } from '../../types'
import './DashboardPage.css'

type Granularity = 'day' | 'week' | 'month'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [timeline, setTimeline] = useState<TimelinePoint[]>([])
  const [aiPerf, setAiPerf] = useState<AiPerformance | null>(null)
  const [branchStats, setBranchStats] = useState<BranchStatRow[]>([])
  const [granularity, setGranularity] = useState<Granularity>('day')
  const [loading, setLoading] = useState(true)
  const [timelineLoading, setTimelineLoading] = useState(false)

  useEffect(() => {
    Promise.all([
      getDashboardSummary(),
      getOrdersTimeline({ granularity }),
      getAiPerformance(),
      getBranchesStats(),
    ])
      .then(([s, t, a, b]) => {
        setSummary(s)
        setTimeline(t)
        setAiPerf(a)
        setBranchStats(b)
      })
      .catch((err) => console.error('[ERROR] Dashboard load failed', err))
      .finally(() => setLoading(false))
  }, [])

  async function handleGranularity(g: Granularity) {
    setGranularity(g)
    setTimelineLoading(true)
    try {
      const t = await getOrdersTimeline({ granularity: g })
      setTimeline(t)
    } catch (err) {
      console.error('[ERROR] Timeline load failed', err)
    } finally {
      setTimelineLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="dash-loading">
        <div className="admin-spinner" />
      </div>
    )
  }

  return (
    <div className="dash">
      <h1 className="dash__title">{t('admin.dashboard.title')}</h1>

      {summary && (
        <>
          {/* Alerts */}
          {summary.pendingBranches > 0 && (
            <div
              className="dash__alert dash__alert--warn"
              onClick={() => navigate('/admin/branches?status=PENDING')}
            >
              <AlertIcon />
              {t('admin.dashboard.pending_branches', { count: summary.pendingBranches })}
              <span className="dash__alert-arrow">→</span>
            </div>
          )}
          {summary.ordersRetryLimitExceeded > 0 && (
            <div
              className="dash__alert dash__alert--error"
              onClick={() => navigate('/admin/orders')}
            >
              <AlertIcon />
              {t('admin.dashboard.retry_exceeded', { count: summary.ordersRetryLimitExceeded })}
              <span className="dash__alert-arrow">→</span>
            </div>
          )}

          {/* Summary cards */}
          <div className="dash__cards">
            <StatCard label={t('admin.dashboard.total_orders')} value={summary.totalOrders} />
            <StatCard label={t('admin.dashboard.today')} value={summary.ordersToday} accent />
            <StatCard label={t('admin.dashboard.this_week')} value={summary.ordersThisWeek} />
            <StatCard label={t('admin.dashboard.active_users')} value={summary.activeUsers} />
            <StatCard label={t('admin.dashboard.active_branches')} value={summary.activeBranches} />
            <StatCard
              label={t('admin.dashboard.ai_error_rate')}
              value={`${summary.aiErrorRatePercent.toFixed(1)}%`}
              warn={summary.aiErrorRatePercent > 5}
            />
          </div>

          {/* Orders by status */}
          <section className="dash__section">
            <h2 className="dash__section-title">{t('admin.dashboard.orders_by_status')}</h2>
            <div className="dash__status-grid">
              {Object.entries(summary.ordersByStatus).map(([status, count]) => (
                <div key={status} className="dash__status-chip">
                  <span className={`dash__status-dot dash__status-dot--${status.toLowerCase()}`} />
                  <span className="dash__status-label">{status}</span>
                  <span className="dash__status-count">{count}</span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}

      {/* Timeline */}
      <section className="dash__section">
        <div className="dash__section-header">
          <h2 className="dash__section-title">{t('admin.dashboard.orders_timeline')}</h2>
          <div className="dash__granularity">
            {(['day', 'week', 'month'] as Granularity[]).map((g) => (
              <button
                key={g}
                className={`dash__gran-btn${granularity === g ? ' dash__gran-btn--active' : ''}`}
                onClick={() => handleGranularity(g)}
                disabled={timelineLoading}
              >
                {t(`admin.dashboard.timeline_${g}`)}
              </button>
            ))}
          </div>
        </div>
        <div className="dash__chart">
          {timelineLoading ? (
            <div className="dash-loading"><div className="admin-spinner" /></div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={timeline} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorErrors" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2d3a" />
                <XAxis dataKey="date" stroke="#5a5d72" tick={{ fontSize: 11 }} />
                <YAxis stroke="#5a5d72" tick={{ fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#1a1d26', border: '1px solid #2a2d3a', borderRadius: 8 }}
                  labelStyle={{ color: '#c8cae0' }}
                  itemStyle={{ color: '#8b8fa8' }}
                />
                <Legend wrapperStyle={{ fontSize: 12, color: '#8b8fa8' }} />
                <Area type="monotone" dataKey="total" stroke="#6366f1" fill="url(#colorTotal)" strokeWidth={2} name={t('admin.dashboard.chart_total')} />
                <Area type="monotone" dataKey="errors" stroke="#ef4444" fill="url(#colorErrors)" strokeWidth={2} name={t('admin.dashboard.chart_errors')} />
                <Area type="monotone" dataKey="retries" stroke="#f59e0b" fill="none" strokeWidth={1.5} strokeDasharray="4 2" name={t('admin.dashboard.chart_retries')} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </section>

      {/* AI Performance */}
      {aiPerf && (
        <section className="dash__section">
          <h2 className="dash__section-title">{t('admin.dashboard.ai_performance')}</h2>
          <div className="dash__ai-grid">
            <AiMetricCard label={t('admin.dashboard.yolo_success')} value={`${aiPerf.yoloSuccessRate.toFixed(1)}%`} ok={aiPerf.yoloSuccessRate >= 90} />
            <AiMetricCard label={t('admin.dashboard.tts_success')} value={`${aiPerf.ttsSuccessRate.toFixed(1)}%`} ok={aiPerf.ttsSuccessRate >= 90} />
            <AiMetricCard
              label={t('admin.dashboard.avg_yolo_time')}
              value={aiPerf.avgYoloProcessingMs != null ? formatMs(aiPerf.avgYoloProcessingMs) : '—'}
            />
            <AiMetricCard
              label={t('admin.dashboard.avg_tts_time')}
              value={aiPerf.avgTtsProcessingMs != null ? formatMs(aiPerf.avgTtsProcessingMs) : '—'}
            />
          </div>
          {aiPerf.top10ErrorReasons.length > 0 && (
            <div className="dash__errors-table">
              <div className="dash__errors-header">
                <span>{t('admin.dashboard.error_reason')}</span>
                <span>{t('admin.dashboard.count')}</span>
                <span>{t('admin.dashboard.last_occurred')}</span>
              </div>
              {aiPerf.top10ErrorReasons.map((e, i) => (
                <div key={i} className="dash__errors-row">
                  <span className="dash__errors-reason">{e.reason}</span>
                  <span className="dash__errors-count">{e.count}</span>
                  <span className="dash__errors-date">{formatDate(e.lastOccurredAt)}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* Branch stats */}
      {branchStats.length > 0 && (
        <section className="dash__section">
          <h2 className="dash__section-title">{t('admin.dashboard.branches')}</h2>
          <div className="dash__table">
            <div className="dash__table-head">
              <span>{t('admin.dashboard.col_branch')}</span>
              <span>{t('admin.dashboard.col_status')}</span>
              <span>{t('admin.dashboard.col_orders')}</span>
              <span>{t('admin.dashboard.col_errors')}</span>
              <span>{t('admin.dashboard.col_active_users')}</span>
            </div>
            {branchStats.map((b) => (
              <div
                key={b.branchId}
                className="dash__table-row"
                onClick={() => navigate(`/admin/branches/${b.branchId}`)}
              >
                <span className="dash__table-name">{b.name}</span>
                <span><StatusBadge status={b.status} /></span>
                <span>{b.ordersCount}</span>
                <span className={b.errorCount > 0 ? 'dash__table-err' : ''}>{b.errorCount}</span>
                <span>{b.activeUsers}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function StatCard({ label, value, accent, warn }: { label: string; value: string | number; accent?: boolean; warn?: boolean }) {
  return (
    <div className={`dash__card${accent ? ' dash__card--accent' : ''}${warn ? ' dash__card--warn' : ''}`}>
      <span className="dash__card-value">{value}</span>
      <span className="dash__card-label">{label}</span>
    </div>
  )
}

function AiMetricCard({ label, value, ok }: { label: string; value: string; ok?: boolean }) {
  return (
    <div className="dash__ai-card">
      <span className={`dash__ai-value${ok === false ? ' dash__ai-value--warn' : ''}`}>{value}</span>
      <span className="dash__ai-label">{label}</span>
    </div>
  )
}

function StatusBadge({ status }: { status: string }) {
  return <span className={`dash__badge dash__badge--${status.toLowerCase()}`}>{status}</span>
}

function AlertIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style={{ flexShrink: 0 }}>
      <path d="M7.5 1L14 13H1L7.5 1Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M7.5 6v3M7.5 10.5v.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  )
}

function formatMs(ms: number): string {
  if (ms >= 60000) return `${(ms / 60000).toFixed(1)}m`
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`
  return `${ms}ms`
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
}
